/**
 * Google Enhanced Ecommerce Object
 *
 * Step logic and functionality for Google Enhanced Ecommerce.
 * https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
 */
const steps = {

    resSignupSteps: {
        optionalExtras: 1,
        aboutYou: 2,
        engineerAppointment: 3,
        orderSummary: 4,
        paymentOptions: 5,
        initialPayment: 6,
        ongoingPayment: 7,
        thanks: 8
    },

    legacyAccountChangeSteps: {
        broadband: 1,
        phone: 2,
        orderSummary: 3,
        selectPaymentMethod: 4,
        enterNewCard: 5,
        ongoingPayment: 6,
        thanks: 7
    },

    accountChangeSteps: {
        address: 1,
        broadband: 2,
        hardware: 3,
        phone: 4,
        mobile: 5,
        phoneLineInfo: 6,
        fttpNeedToKnow: 7,
        engineerAppointment: 8,
        orderSummary: 9,
        thanks: 10
    },

    /**
     * Get's the name of the current signup step
     *
     * @returns  {string|null}
     */
    getSignupStep: () => {
        const matches = /^\/signup\/options\/([^\/]+)$/gi.exec(window.location.pathname);

        return matches ? matches[1] : null;
    },

    /**
     * Get the funnel step index for the specified journey
     *
     * @param  {string} pageName
     * @returns  {int}
     */
    getFunnelStep: (pageName) => {

        if (!pageName) {
            return;
        }

        switch (gaEcommerce.core.getJourney()) {
            case 'broadband|sign-up|residential':
                return gaEcommerce.steps.getResSignupFunnelStep(pageName);
            case 'broadband|change-product|residential':
                return gaEcommerce.steps.getLegacyAccountChangeFunnelStep(pageName);
            case 'broadband|update|residential':
                return gaEcommerce.steps.getAccountChangeFunnelStep(pageName);
        }
    },

    /**
     * Get the funnel step index for res signup
     *
     * @param  {string} pageName
     * @returns  {int}
     */
    getResSignupFunnelStep: (pageName) => {

        // Ensures pagename in referrer is used in portal too
        if (pageName.indexOf('apps/paymentdetails/') !== -1) {
            pageName = pageName.split('/');
            pageName = '/' + pageName[pageName.length - 2] + '/';
        }

        switch (pageName) {
            case '/signup/options/optional-extras':
                return gaEcommerce.steps.resSignupSteps.optionalExtras;
            case '/signup/about-you/':
                return gaEcommerce.steps.resSignupSteps.aboutYou;
            case '/signup/engineer-appointing/':
                return gaEcommerce.steps.resSignupSteps.engineerAppointment;
            case '/signup/order-summary/':
                return gaEcommerce.steps.resSignupSteps.orderSummary;
            case '/signup/payment-options/':
                return gaEcommerce.steps.resSignupSteps.paymentOptions;
            case '/initial-payment/':
                return gaEcommerce.steps.resSignupSteps.initialPayment;
            case '/ongoing-payment/':
                return gaEcommerce.steps.resSignupSteps.ongoingPayment;
            case '/signup/thanks/':
                return gaEcommerce.steps.resSignupSteps.thanks;
        }
    },

    /**
     * Get the funnel step index for legacy account change
     *
     * @param  {string} pageName
     * @returns  {int}
     */
    getLegacyAccountChangeFunnelStep: (pageName) => {
        // strip dynamic number (of variable length) from path
        // example path /apps/account/change/376/C2FBroadband/
        pageName = pageName.replace(/\/\d+/, '');

        // strip forward slash from end of path
        pageName = pageName.replace(/\/$/, '');

        // strip query params
        pageName = pageName.replace(/\?.*\=.*$/i, '');


        /* Ongoing payment, summary, thanks pages share paths
           so use query params to identify the page */
        const queryParams = window.location.search,
              isThanksPage = (
                queryParams.indexOf('intIdempotenceId') !== -1
                || queryParams.indexOf('strIdempotenceAction') !== -1
              );

        if (isThanksPage) {
            return gaEcommerce.steps.legacyAccountChangeSteps.thanks;
        }

        switch (pageName) {
            case '/apps/account/change/C2FBroadband':
                return gaEcommerce.steps.legacyAccountChangeSteps.broadband;
            case '/apps/account/change/C2FHomephone':
                return gaEcommerce.steps.legacyAccountChangeSteps.phone;
            case '/apps/account/change/C2FSummary':
                return gaEcommerce.steps.legacyAccountChangeSteps.orderSummary; 
            case '/apps/paymentdetails/takepayment/PaymentDetails':
                return gaEcommerce.steps.legacyAccountChangeSteps.selectPaymentMethod;
            case '/apps/paymentdetails/takepayment/CreditCardDetails':
                return gaEcommerce.steps.legacyAccountChangeSteps.enterNewCard;
            case '/apps/account/change/PaymentDetails':
                return gaEcommerce.steps.legacyAccountChangeSteps.ongoingPayment;
        }
    },

    /**
     * Get the funnel step index for React account change
     *
     * @param  {string} pageName
     * @returns  {int}
     */
    getAccountChangeFunnelStep: (pageName) => {

        // strips query params & trailing slash
        pageName = pageName
            .replace(/\?.*\=.*$/i, '')
            .replace(/\/$/, '')
            .replace('/member-centre/offers-and-upgrades/', '');

        switch (pageName) {
            case 'set-address':
                return gaEcommerce.steps.accountChangeSteps.address;
            case 'choose-broadband':
                return gaEcommerce.steps.accountChangeSteps.broadband;
            case 'choose-hardware':
                return gaEcommerce.steps.accountChangeSteps.hardware;
            case 'choose-call-plan':
                return gaEcommerce.steps.accountChangeSteps.phone;
            case 'choose-mobile':
                return gaEcommerce.steps.accountChangeSteps.mobile;
            case 'phone-line-info':
                return gaEcommerce.steps.accountChangeSteps.phoneLineInfo; 
            case 'fttp-need-to-know':
                return gaEcommerce.steps.accountChangeSteps.fttpNeedToKnow; 
            case 'book-engineer':
                return gaEcommerce.steps.accountChangeSteps.engineerAppointment; 
            case 'confirm-products':
                return gaEcommerce.steps.accountChangeSteps.orderSummary; 
            case 'summary':
                return gaEcommerce.steps.accountChangeSteps.thanks; 
        }
    },

    /**
     * This method is triggered after a step change occurs
     * - it re-initialises the necessary modules for the new step
     *
     * @returns {void}
     */
    signupStepChange: () => {
        // Fire product impressions when changing steps in signup
        gaEcommerce.impressions.initialise();

        if (gaEcommerce.steps.getSignupStep() === 'optional-extras') {
            // If optional extras page fire a checkout event
            gaEcommerce.checkout.initialise();
        }
    }
};

export default steps;

